<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col overflow-hidden">
      <div class="px-4 sm:px-6 pt-3 pb-1">
        <div class="flex items-baseline gap-2 flex-wrap">
          <h2 class="text-base font-bold text-[#0d4169] text-left m-0">
            {{ resolvedCompanyName }}
          </h2>
          <span class="text-sm font-bold text-slate-600">Evaluation Report</span>
        </div>
      </div>

      <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

      <form class="px-4 sm:px-6 pb-5 pt-2" @submit.prevent="generateReport">
        <div class="flex flex-col lg:flex-row lg:items-end lg:flex-wrap gap-x-4 gap-y-3">
          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[170px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">
              Start Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.startDate"
              type="date"
              required
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[170px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">End Date</label>
            <input
              v-model="form.endDate"
              type="date"
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[200px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Employee Name</label>
            <input
              v-model="form.employeeName"
              type="text"
              placeholder="Optional"
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[160px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">By Status</label>
            <select
              v-model="form.status"
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto">
            <button
              type="submit"
              :disabled="isLoadingReport"
              class="w-full sm:w-auto px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span v-if="isLoadingReport" class="inline-flex items-center gap-2">
                <span class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
                Generating...
              </span>
              <span v-else>Generate</span>
            </button>
          </div>
        </div>
      </form>

      <div class="px-2 pb-6 pt-2">
        <template v-if="showReportTable">
          <div v-if="isLoadingReport" class="py-12 flex justify-center items-center">
            <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
          </div>

          <div v-else class="w-full">
            <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0 px-2 sm:px-4">
              {{ reportPeriodTitle }}
            </p>

            <div class="eva-table-scroll relative overflow-x-auto overflow-y-auto min-h-[220px] max-h-[calc(100vh-280px)] px-1 sm:px-2">
              <table class="eva-table w-full min-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th class="eva-col-no">No.</th>
                    <th>Employee Name</th>
                    <th>Evaluation Date</th>
                    <th>Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in reportRows"
                    :key="row.id || row.no"
                    class="hover:bg-slate-50 transition-colors border-b"
                  >
                    <td class="eva-col-no">{{ row.no }}</td>
                    <td>{{ row.employeeName || '—' }}</td>
                    <td>{{ formatDisplayDate(row.evaluationDate) }}</td>
                    <td>
                      <button
                        type="button"
                        class="result-link"
                        :disabled="isLoadingDetail"
                        @click="openResultDetail(row)"
                      >
                        {{ row.result || '—' }}
                      </button>
                    </td>
                    <td>
                      <span class="status-pill" :class="statusClass(row)">
                        {{ row.status || statusLabel(row.isApproved) }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="reportRows.length === 0">
                    <td colspan="5" class="text-center text-slate-400 italic py-5">
                      No evaluations found for the selected filters.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <p
          v-else
          class="text-sm text-slate-500 text-center py-10 m-0 italic"
        >
          Select a Start Date and click Generate to view the evaluation report.
        </p>
      </div>
    </div>

    <div
      v-if="isDetailOpen"
      class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
      @click.self="closeDetailModal"
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
        <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
          <h2 class="text-lg font-bold m-0">Evaluation Detail</h2>
          <button
            type="button"
            class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
            @click="closeDetailModal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-6 sm:p-8 relative">
          <div
            v-if="isLoadingDetail"
            class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-b-xl"
          >
            <div class="h-8 w-8 border-4 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-3 mb-5">
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Employee Name</label>
              <input
                :value="detail.employeeName || '—'"
                type="text"
                readonly
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Evaluation Date</label>
              <input
                :value="formatDisplayDate(detail.evaluationDate)"
                type="text"
                readonly
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Result</label>
              <input
                :value="detail.result || '—'"
                type="text"
                readonly
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
              >
            </div>
          </div>

          <div class="mb-2">
            <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">
              Evaluation Criteria
            </span>
          </div>

          <div class="w-full overflow-x-auto mb-2">
            <table class="w-full min-w-[520px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm detail-table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Criteria Name</th>
                  <th>Grade</th>
                  <th>Max</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in detailItems"
                  :key="item.id || item.no"
                  class="hover:bg-slate-100/50 transition-colors border-b"
                >
                  <td>{{ item.no }}</td>
                  <td class="name-cell">{{ item.criteriaName || '—' }}</td>
                  <td>{{ formatPoint(item.empPoint) }}</td>
                  <td>{{ formatPoint(item.maxPoint) }}</td>
                </tr>
                <tr v-if="!isLoadingDetail && detailItems.length === 0">
                  <td colspan="4" class="text-center text-slate-400 italic py-8">
                    No evaluation items found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-6 pt-5 border-t border-slate-100 flex justify-end">
            <button
              type="button"
              class="px-5 py-2 bg-slate-100 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-200 transition-all"
              @click="closeDetailModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../../finance/FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = reactive({
  startDate: '',
  endDate: '',
  employeeName: '',
  status: ''
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

const isLoadingReport = ref(false)
const reportRows = ref([])
const hasGenerated = ref(false)
let reportRequestId = 0

const isDetailOpen = ref(false)
const isLoadingDetail = ref(false)
const detail = reactive({
  id: null,
  employeeName: '',
  evaluationDate: '',
  result: ''
})
const detailItems = ref([])
let detailRequestId = 0

const showReportTable = computed(() => hasGenerated.value)

function formatDisplayDate(isoDate) {
  if (!isoDate) return '—'
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatPoint(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0'
  if (Number.isInteger(n)) return String(n)
  return String(Number(n.toFixed(1)))
}

function statusLabel(isApproved) {
  const n = Number(isApproved)
  if (n === 1) return 'Accepted'
  if (n === -1) return 'Rejected'
  return 'Pending'
}

function statusClass(row) {
  const label = String(row?.status || statusLabel(row?.isApproved)).toLowerCase()
  if (label === 'accepted') return 'accepted'
  if (label === 'rejected') return 'rejected'
  return 'pending'
}

const reportPeriodTitle = computed(() => {
  const start = form.startDate ? formatDisplayDate(form.startDate) : ''
  const end = form.endDate ? formatDisplayDate(form.endDate) : ''
  let title = 'Evaluation Report'
  if (start && end) title = `Evaluation Report for ${start} - ${end}`
  else if (start) title = `Evaluation Report from ${start}`
  if (form.status) title += ` (${form.status})`
  if (form.employeeName.trim()) title += ` — ${form.employeeName.trim()}`
  return title
})

function resetDetail() {
  detail.id = null
  detail.employeeName = ''
  detail.evaluationDate = ''
  detail.result = ''
  detailItems.value = []
}

function closeDetailModal() {
  isDetailOpen.value = false
  isLoadingDetail.value = false
  resetDetail()
}

async function openResultDetail(row) {
  const evaluationId = row?.id
  if (!evaluationId) {
    showToast('Evaluation id is missing.', 'error')
    return
  }

  const requestId = ++detailRequestId
  isDetailOpen.value = true
  isLoadingDetail.value = true
  detail.id = evaluationId
  detail.employeeName = row.employeeName || ''
  detail.evaluationDate = row.evaluationDate || ''
  detail.result = row.result || ''
  detailItems.value = []

  try {
    const response = await apiFetch(`/hr/evaluation-report/${evaluationId}`, {
      method: 'GET'
    })
    if (requestId !== detailRequestId) return

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      showToast(data?.message || 'Failed to load evaluation detail.', 'error')
      closeDetailModal()
      return
    }

    const data = await response.json().catch(() => null)
    if (!data) {
      showToast('Failed to load evaluation detail.', 'error')
      closeDetailModal()
      return
    }

    detail.employeeName = data.employeeName || row.employeeName || ''
    detail.evaluationDate = data.evaluationDate || row.evaluationDate || ''
    detail.result = data.result || row.result || ''
    detailItems.value = Array.isArray(data.items) ? data.items : []
  } catch {
    if (requestId !== detailRequestId) return
    showToast('Failed to load evaluation detail.', 'error')
    closeDetailModal()
  } finally {
    if (requestId === detailRequestId) {
      isLoadingDetail.value = false
    }
  }
}

async function generateReport() {
  const startDate = String(form.startDate || '').trim()
  if (!startDate) {
    showToast('Start Date is required.', 'error')
    return
  }

  const endDate = String(form.endDate || '').trim()
  if (endDate && endDate < startDate) {
    showToast('End Date cannot be before Start Date.', 'error')
    return
  }

  const requestId = ++reportRequestId
  isLoadingReport.value = true
  hasGenerated.value = true

  try {
    const params = new URLSearchParams()
    params.set('startDate', startDate)
    if (endDate) params.set('endDate', endDate)
    const employeeName = String(form.employeeName || '').trim()
    if (employeeName) params.set('employeeName', employeeName)
    if (form.status) params.set('status', form.status)

    const response = await apiFetch(`/hr/evaluation-report?${params.toString()}`, {
      method: 'GET'
    })
    if (requestId !== reportRequestId) return

    if (!response.ok) {
      reportRows.value = []
      const data = await response.json().catch(() => ({}))
      showToast(data?.message || 'Failed to load evaluation report.', 'error')
      return
    }

    const rows = await response.json().catch(() => [])
    reportRows.value = Array.isArray(rows) ? rows : []
  } catch {
    if (requestId !== reportRequestId) return
    reportRows.value = []
    showToast('Failed to load evaluation report.', 'error')
  } finally {
    if (requestId === reportRequestId) {
      isLoadingReport.value = false
    }
  }
}
</script>

<style scoped>
.eva-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
}

.eva-table th,
.eva-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
}

.eva-table thead th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 20;
}

.eva-col-no {
  width: 2.75rem;
  min-width: 2.75rem;
  max-width: 2.75rem;
  text-align: center;
}

.eva-table tbody td {
  text-align: left;
}

.eva-table tbody tr:hover {
  background: #f8fafc;
}

.result-link {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: #0d4169;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
}

.result-link:hover {
  color: #0a3354;
}

.result-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4.5rem;
  padding: 0.15rem 0.55rem;
  border-radius: 9999px;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
}

.status-pill.pending {
  background: #fef3c7;
  color: #92400e;
}

.status-pill.accepted {
  background: #dcfce7;
  color: #166534;
}

.status-pill.rejected {
  background: #fee2e2;
  color: #991b1b;
}

.detail-table th,
.detail-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  text-align: left;
  vertical-align: middle;
}

.detail-table thead th {
  background: #f1f5f9;
  font-weight: 700;
  color: #0d4169;
}

.detail-table .name-cell {
  white-space: normal;
  min-width: 180px;
}

@media (max-width: 640px) {
  .eva-table th,
  .eva-table td {
    padding: 5px 8px;
    font-size: 0.7rem;
  }
}
</style>
